// src/components/common/OfflineIndicator.tsx
// Persistent banner at the top of the layout showing offline/syncing/error states.
// Slides in from top with smooth animation. RTL-aware. Auto-dismisses success after 3s.

import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, LinearProgress, Chip } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useOffline } from '../../hooks/useOffline';
import translations, { Language } from '../../translations';

const getT = (key: string): string => {
  const lang = (localStorage.getItem('appLanguage') || 'ar') as Language;
  return translations[lang]?.[key] || translations['ar']?.[key] || key;
};

type BannerState = 'hidden' | 'offline' | 'syncing' | 'success' | 'error';

const OfflineIndicator: React.FC = () => {
  const { isOnline, pendingCount, failedCount, isSyncing, retryFailed } = useOffline();
  const [bannerState, setBannerState] = useState<BannerState>('hidden');
  const [showSuccess, setShowSuccess] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setBannerState('offline');
      setWasOffline(true);
    } else if (isSyncing) {
      setBannerState('syncing');
    } else if (failedCount > 0) {
      setBannerState('error');
    } else if (wasOffline && isOnline && !isSyncing) {
      // Just came back online and finished syncing
      setBannerState('success');
      setShowSuccess(true);
      setWasOffline(false);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setBannerState('hidden');
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setBannerState('hidden');
    }
  }, [isOnline, isSyncing, failedCount, wasOffline]);

  if (bannerState === 'hidden') return null;

  const configs: Record<Exclude<BannerState, 'hidden'>, {
    bg: string;
    icon: React.ReactNode;
    text: string;
    subtext?: string;
    pulsing?: boolean;
  }> = {
    offline: {
      bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      icon: <WifiOffIcon sx={{ fontSize: 20 }} />,
      text: getT('offline.banner.noConnection'),
      subtext: pendingCount > 0
        ? getT('offline.banner.pendingOps').replace('{{count}}', String(pendingCount))
        : getT('offline.banner.workingLocally'),
    },
    syncing: {
      bg: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      icon: <SyncIcon sx={{ fontSize: 20, animation: 'spin 1s linear infinite' }} />,
      text: getT('offline.banner.syncing'),
      pulsing: true,
    },
    success: {
      bg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      text: getT('offline.banner.syncComplete'),
    },
    error: {
      bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      icon: <ErrorOutlineIcon sx={{ fontSize: 20 }} />,
      text: getT('offline.banner.syncFailed').replace('{{count}}', String(failedCount)),
    },
  };

  const config = configs[bannerState];

  return (
    <Box
      id="offline-indicator"
      sx={{
        background: config.bg,
        color: '#fff',
        px: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        animation: 'slideDown 0.3s ease-out',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        '@keyframes slideDown': {
          from: { transform: 'translateY(-100%)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
        '@keyframes spin': {
          from: { transform: 'rotate(0deg)' },
          to: { transform: 'rotate(360deg)' },
        },
        '@keyframes pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.7 },
        },
        ...(config.pulsing && { animation: 'slideDown 0.3s ease-out, pulse 1.5s ease-in-out infinite' }),
      }}
    >
      {config.icon}

      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
        {config.text}
      </Typography>

      {config.subtext && (
        <Chip
          label={config.subtext}
          size="small"
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: '#fff',
            fontSize: '0.75rem',
            height: 22,
            fontWeight: 500,
          }}
        />
      )}

      {bannerState === 'syncing' && (
        <LinearProgress
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            '& .MuiLinearProgress-bar': { bgcolor: 'rgba(255,255,255,0.6)' },
            bgcolor: 'rgba(255,255,255,0.15)',
          }}
        />
      )}

      {bannerState === 'error' && (
        <IconButton
          size="small"
          onClick={() => retryFailed()}
          sx={{ color: '#fff', ml: 0.5, p: 0.5 }}
          title={getT('offline.banner.retry')}
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}

      {bannerState === 'success' && (
        <IconButton
          size="small"
          onClick={() => setBannerState('hidden')}
          sx={{ color: '#fff', ml: 0.5, p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
};

export default OfflineIndicator;
